import React, { useState, useEffect, useRef } from "react";
import NormalTable from "../../../../utility/table/NormalTable.jsx";
import { useTranslation } from "react-i18next";
import CommonComponent from "../../CommonComponent";
import useTimeSalesAnalysis from "./useTimeSalesAnalysis";
import usePersistentDateRange from "../../Common/usePersistentDateRange";
import { getFilterOptions } from "../../Common/getFilterOptions";
import TimeSalesChart from "./ChartLayout/TimeSalesChart";
import WeeklySalesDistribution from "./ChartLayout/WeeklySalesDistribution";
import NoDataFound from "../NoDataFound/NoDataFound";
import Spinner from "../NoDataFound/Spinner";
import DashboardReady from "../DashboardReady/DashboardReady";
import categorySelectionDynamic from "../../Common/categorySelectionDynamic";
import ViewTypeSelect from "../ViewTypeSelect";
import { useOption } from "../../../../context/OptionContext.js";
import { usePermissions } from "../../../../context/PermissionContext.js";
import PermissionControlledCard from "../../../../common/PermissionControlledCard.jsx";
import CoastDistributionChart from "../RegionalLevelSales/ChartLayout/CoastDistributionChart.jsx";
import AllowedCard from "../../../../common/AllowedCard.jsx";
import { addColumn } from "../../Common/addColumn.js";

const TimeSalesAnalysis = () => {
  const {
    timeBasedlState,
    weeklySalesDistriutionState,
    timeSalesAnalysisState,
    getTimeBased,
    getWeeklySalesDistriution,
    getTimeSalesAnalysis,
    timeBasedFilter,
    setTimeBasedFilter,
    timeSalesAnalysisSequence,
    timeSalesAnalysisLoaded,
    loadingState, canShowTable, combinedPermission, accessDenied
  } = useTimeSalesAnalysis();
  const { selectedOption } = useOption();
  const { t } = useTranslation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [viewType, setViewType] = useState(
    localStorage.getItem("viewType") || "category"
  );

  const [selectedIds, setSelectedIds] = useState({
    productId: [],
    categoryId: [],
    parentProductId: [],
  });

  const handleViewTypeChange = (e) => {
    setViewType(e.target.value);
    localStorage.setItem("viewType", e.target.value);
  };

  const { selectedRange, handleDateChange, formattedStart, formattedEnd } =
    usePersistentDateRange();

  const hasMountedTimeBased = useRef(false);
  const hasMountedWeeklySales = useRef(false);
  const hasMountedTimeAnalysis = useRef(false);
  const permission = usePermissions();
  // 1. When timeBasedFilter changes, only call related API (skip on first mount)

  useEffect(() => {
    const viewFormat = permission?.data?.time_sales_analysis?.view_format;
    if (!viewFormat) return;

    const savedView = localStorage.getItem("viewType");
    const isProductAllowed = viewFormat?.product_wise?.allowed;
    const isCategoryAllowed = viewFormat?.category_wise?.allowed;

    let newView = savedView;

    // ✅ Switch only if current view is not allowed
    if (savedView === "category" && !isCategoryAllowed && isProductAllowed) {
      newView = "product";
    }
    else if (savedView === "product" && !isProductAllowed && isCategoryAllowed) {
      newView = "category";
    }

    if (newView !== savedView) {
      setViewType(newView);
      localStorage.setItem("viewType", newView);
    }
  }, [
    permission?.data?.time_sales_analysis?.view_format?.product_wise?.allowed,
    permission?.data?.time_sales_analysis?.view_format?.category_wise?.allowed
  ]);

  const handleColumnChange = async(selectedCols, allColumns) =>{
     const formattedColumns = allColumns
        .filter(col => col !== "id")
        .map(col => ({
          key: col,
          visible: selectedCols.includes(col)
        }));
  
      await addColumn({
        data: {
          section_name: "time_sales_analysis",
          columns: formattedColumns
        }
      });
 getTimeSalesAnalysis({ formattedEnd, formattedStart });
}

useEffect(() => {
  if (!hasMountedTimeBased.current) {
    hasMountedTimeBased.current = true;
    return;
  }

  getTimeBased({
    timeBasedFilter,
    formattedStart,
    formattedEnd,
    selectedIds,
  });
}, [
  timeBasedFilter,
  formattedStart,
  formattedEnd,
  viewType,
  selectedOption?.id,
]);



  const {
    categoryId,
    categoryName,
    handleCategoryClick,
    onBreadcrumbClick,
    handleParentProductClick,
  } = categorySelectionDynamic(
    [getTimeBased, getWeeklySalesDistriution, getTimeSalesAnalysis],
    formattedStart,
    formattedEnd
  );

  const shouldFetchData =
    !isButtonDisabled &&
    (
      localStorage.getItem("viewType") === 'product' ? (selectedIds.productId.length === 0) : (selectedIds.categoryId.length === 0 && categoryId === null)

   );


  // 2. When date range changes, call ALL APIs (including the filtered one)
  useEffect(() => {
    if (formattedStart && formattedEnd) {
      if(shouldFetchData){
      getTimeBased({ timeBasedFilter, formattedEnd, formattedStart });
      getWeeklySalesDistriution({ formattedEnd, formattedStart });
      }
    }
  }, [formattedStart, formattedEnd, viewType, selectedOption?.id,isButtonDisabled]);
 useEffect(()=>{
  if (formattedStart && formattedEnd) {
      getTimeSalesAnalysis({ formattedEnd, formattedStart });
  }
 },[formattedStart, formattedEnd, viewType, selectedOption?.id])
  useEffect(() => {
    setIsButtonDisabled(false); // Re-enable when user changes selection
  }, [selectedIds]);

  // useEffect(() => {
  //   // Enable button when user selects again
  //   if (
  //     selectedIds?.parentProductId?.length > 0 ||
  //     selectedIds?.productId?.length > 0 ||
  //     selectedIds?.categoryId?.length > 0
  //   ) {
  //     setIsButtonDisabled(false);
  //   }
  
  //   // Clear productId if parent product is selected
  //   if (
  //     selectedIds?.parentProductId?.length > 0 &&
  //     selectedIds?.productId?.length > 0
  //   ) {
  //     setSelectedIds((prev) => ({
  //       ...prev,
  //       productId: [],
  //     }));
  //   }
  // }, [
  //   selectedIds?.parentProductId?.length,
  //   selectedIds?.productId?.length,
  //   selectedIds?.categoryId?.length,
  // ]);
  

  const handleSelectionChange = (ids) => {
    
    const hasNoSelection =
      ids.productId.length === 0 && ids.categoryId.length === 0;

    if (
      selectedIds.productId.length + selectedIds.categoryId.length > 0 &&
      hasNoSelection
    ) {
      window.location.reload();
    }

    setSelectedIds(ids);
  };

  const handleReflectSelection = (e) => {
    e.preventDefault(); // prevents default button behavior
    setIsButtonDisabled(true);
    const effectiveSelectedIds =
    (viewType === 'category' && selectedIds.categoryId.length > 0)
      ? { ...selectedIds, productId: [] }
      : selectedIds;
    getTimeBased({
      timeBasedFilter,
      formattedEnd,
      formattedStart,
      selectedIds:selectedIds,
    });
    // getTimeSalesAnalysis({formattedEnd, formattedStart, selectedIds:selectedIds});
    getWeeklySalesDistriution({ formattedEnd, formattedStart, selectedIds:selectedIds });
  };


  const dashboardReady = localStorage.getItem("dashboard-ready") === "true";
  const options = getFilterOptions(formattedStart, formattedEnd, "times-sales");
  const downloadBtnPermission = permission?.data?.time_sales_analysis?.table_section?.download_option?.display && permission?.data?.time_sales_analysis?.table_section?.download_option?.allowed

  if (accessDenied) {
    return (
      <div className='d-flex justify-content-center' style={{ textAlign: "center", padding: "30px", fontWeight: "500" }}>
        <AllowedCard />
      </div>
    )
  }

  const isHideTimeGraph = ((permission?.data?.time_sales_analysis?.graphs?.bar_graph?.display) && (!permission?.data?.time_sales_analysis?.graphs?.bar_graph?.allowed))

  return (
    <>
      <CommonComponent
        defaultRange={selectedRange}
        onRangeChange={handleDateChange}
        categoryName={categoryName}
        categoryId={categoryId}
        onBreadcrumbClick={onBreadcrumbClick}
        viewType={viewType}
      />
      {!dashboardReady ? (
        <DashboardReady />
      ) : (
        <section className="section dashboard py-2 px-2">
          <div className="container-fluid">
            <div className="row px-1 px-md-0">
              <div className="col-lg-12 mx-md-0 p-md-0">
                <div className="row graph-section mx-md-1">
                  {/* Total Revenue */}
                  <PermissionControlledCard
                    permission={permission?.data?.time_sales_analysis?.graphs?.bar_graph}
                    colClass="col-12 col-md-6 col-lg-8">

                    <div className="col-12">
                      <div className="card total-revenue">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="kpi-heading">
                              {t("timeSalesAnalysis.timeBasedSalesAnalysis")}
                            </h5>
                            <div className="d-flex justify-content-end">
                              <select
                                className="form-select w-auto select-day graph-filter kpi-name"
                                value={timeBasedFilter}
                                onChange={(e) => {
                                  e.preventDefault();
                                  setTimeBasedFilter(e.target.value);
                                }}
                              >
                                {options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {loadingState?.timeBasedlState ? (
                            <Spinner height={300} />
                          ) : (
                            !isHideTimeGraph ? (
                              <TimeSalesChart chartData={timeBasedlState} />
                            ):<div style={{height:"300px"}}></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </PermissionControlledCard>
                  <PermissionControlledCard
                    permission={permission?.data?.time_sales_analysis?.graphs?.pie_graph}
                    colClass="col-12 col-md-6 col-lg-4"
                  >
                    {/* Weekly Sales Distribution */}

                    <div className="col-12">
                      <div className="card total-revenue">
                        <div className="card-body d-flex flex-column">
                          <h5 className="kpi-heading mb-3">
                            {t("timeSalesAnalysis.weeklySalesDistribution")}
                          </h5>
                          <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                            <div style={{ width: "100%" }}>
                              {loadingState?.weeklySalesDistriutionState ? (
                                <Spinner type="pie" height={300} />
                              ) : (
                                <WeeklySalesDistribution
                                  chartData={weeklySalesDistriutionState}
                                />
                                //  <CoastDistributionChart chartData={weeklySalesDistriutionState} />

                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </PermissionControlledCard>
                </div>
              </div>
              <div className="col-12 px-0 mt-2 mx-md-1">
                <div className="card">
                  <div className="card-body h-auto">
                    <div className="justify-content-between align-items-baseline">
                      <div className="d-flex justify-content-between align-items-baseline">
                        <h5 className="kpi-heading">
                          {t("timeSalesAnalysis.timeSalesAnalysis")}
                        </h5>
                        <PermissionControlledCard
                          permission={permission?.data?.time_sales_analysis?.table_section.table_view}
                          colClass="col-0"
                        >
                          <ViewTypeSelect
                            value={viewType}
                            onChange={handleViewTypeChange}
                            permissions={
                              permission?.data?.time_sales_analysis?.view_format
                            }
                          />
                        </PermissionControlledCard>
                      </div>
                      {timeSalesAnalysisLoaded &&
                        Array.isArray(timeSalesAnalysisState) &&
                        timeSalesAnalysisState.length === 0 ? (
                        <div className='d-flex justify-content-center' style={{ textAlign: "center", padding: "30px", fontWeight: "500" }}>
                          No data found
                        </div>
                      ) : (
                        canShowTable && (
                          <PermissionControlledCard
                            permission={combinedPermission}
                            colClass="col-12"
                          >
                            <NormalTable
                              tableId="timeSalesAnalysisId"
                              data={timeSalesAnalysisState}
                              selectedIds={selectedIds}
                              onSelectionChange={handleSelectionChange}
                              prioritizeSelected={isButtonDisabled}
                              onCategoryClick={handleCategoryClick}
                              onParentProductClick={handleParentProductClick}
                              loading={loadingState.timeSalesAnalysisState}
                              sequence={timeSalesAnalysisSequence}
                              downloadBtnPermission={downloadBtnPermission}
                              isProductwiseTable={viewType === 'product'}
                              onColumnChange={handleColumnChange}
                              showSelectColumn={true}
                              section={"time_sales_analysis"}
                            />
                          </PermissionControlledCard>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      {selectedIds.productId.length + selectedIds.categoryId.length > 0 && (
        <button
          className="floating-btn-select"
          onClick={handleReflectSelection}
          disabled={isButtonDisabled}
        >
          ({selectedIds.productId.length + selectedIds.categoryId.length}){" "}
          {t("checkboxSelect.heading")}
        </button>
      )}
    </>
  );
};

export default TimeSalesAnalysis;
